import { Box, Divider, Stack } from "@mui/material";
import { FormikTextField } from "@shared";

import { AttendanceType } from "@/api";
import { PlanningMode } from "@/common/components/appointments/plan-attendance/PlanAttendanceDialog.types";
import { purposeAttendanceType } from "@/common/components/appointments/shared/AppointmentForm.utils";
import { FormikCounselorSelect } from "@/common/components/counselor-select/FormikCounselorSelect";
import { IOptionItem } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete.types";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDatePicker } from "@/common/components/fields/formik/formik-date-picker/FormikDatePicker";
import { useTranslation } from "@/translations";

import { PlanningModeSelect } from "../planning-mode-select/PlanningModeSelect";
import { useMultiDayForm } from "./hooks/useMultiDayForm";

type MultiDayFormResult = ReturnType<typeof useMultiDayForm>;

interface Props extends MultiDayFormResult {
  onModeChange: (mode: PlanningMode) => void;
}

export function MultiDayForm({ formik, purposes, purposeOptions, onModeChange }: Props) {
  const { t } = useTranslation();

  const isAbsent = formik.values.attendanceType === AttendanceType.Absent;

  const handlePurposeChange = async (_event: unknown, value: IOptionItem | null) => {
    const attendanceType = purposeAttendanceType(value?.id, purposes);
    await formik.setValues({
      ...formik.values,
      purposeKey: value?.id ?? undefined,
      attendanceType,
      substituteCounselorId: attendanceType === AttendanceType.Absent ? formik.values.substituteCounselorId : null,
    });
  };

  return (
    <Stack spacing={3} width={1200}>
      <Box display="flex" alignItems="flex-start" gap={2} pt={1}>
        <PlanningModeSelect value={PlanningMode.MultiDay} onChange={onModeChange} />
        <FormikDatePicker formik={formik} fieldName="dateFrom" label={t("calendar.planAttendance.dateFrom")} required />
        <FormikDatePicker formik={formik} fieldName="dateTo" label={t("calendar.planAttendance.dateTo")} required />
      </Box>
      <Divider />
      <Box display="flex" alignItems="flex-start" gap={2}>
        <FormikAutocompleteSelect
          formik={formik}
          fieldName="purposeKey"
          label={t("calendar.planAttendance.purpose")}
          options={purposeOptions}
          required
          onChange={(event, value) => void handlePurposeChange(event, value)}
        />
        {isAbsent && (
          <FormikCounselorSelect
            formik={formik}
            fieldName="substituteCounselorId"
            label={t("calendar.planAttendance.substitute")}
          />
        )}
        <FormikTextField formik={formik} fieldName="note" label={t("calendar.planAttendance.note")} />
      </Box>
    </Stack>
  );
}
